// Inventario › Productos y stock: búsqueda server-side por SKU, nombre o código de barras; a la
// derecha, presentaciones y stock FÍSICO / RESERVADO / DISPONIBLE por ubicación (unidad base).
import { useState } from 'react';
import {
  useBuscarProductos,
  useDebounce,
  useDisponibilidad,
  usePresentaciones,
  useUbicaciones,
} from '../api/consultas';
import type { ProductoEncontrado } from '../api/contratos';
import { Grilla, type Columna } from '../comun/Grilla';
import { PanelError } from '../comun/Estados';
import { decimal } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { TablaStock } from '../ventas/BuscadorProducto';

export function ConsultaProductos({
  activa,
  textoInicial = '',
  alAbrirProducto,
}: {
  activa: boolean;
  /** Ctrl+K: el producto elegido en el buscador global llega ya buscado. */
  textoInicial?: string;
  /** Enter / doble clic: abre la ficha del producto en su pestaña. */
  alAbrirProducto?: (p: ProductoEncontrado) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [texto, setTexto] = useState(textoInicial);
  const [ubicacionId, setUbicacionId] = useState('');
  const [sel, setSel] = useState<ProductoEncontrado | null>(null);
  const q = useBuscarProductos(empresaId, useDebounce(texto));
  const ubicaciones = useUbicaciones(empresaId);
  const stock = useDisponibilidad(empresaId, { ubicacionId: ubicacionId || null });
  const pres = usePresentaciones(empresaId, sel?.id ?? null);
  const d = (id: string) => stock.data?.find((f) => f.productoId === id);

  const columnas: Columna<ProductoEncontrado>[] = [
    {
      id: 'sku',
      titulo: 'SKU',
      celda: (p) => <span className="mono">{p.sku}</span>,
      ancho: '9rem',
    },
    { id: 'nombre', titulo: 'Producto', celda: (p) => p.nombre },
    { id: 'ub', titulo: 'U. base', celda: (p) => p.unidad_base_codigo, ancho: '5rem' },
    { id: 'estado', titulo: 'Estado', celda: (p) => p.estado, ancho: '6rem' },
    ...(ubicacionId
      ? ([
          {
            id: 'fis',
            titulo: 'Físico',
            alinear: 'der',
            ancho: '6rem',
            celda: (p) => <span className="mono">{decimal(d(p.id)?.fisico ?? '0')}</span>,
          },
          {
            id: 'res',
            titulo: 'Reservado',
            alinear: 'der',
            ancho: '6rem',
            celda: (p) => <span className="mono">{decimal(d(p.id)?.reservado ?? '0')}</span>,
          },
          {
            id: 'dis',
            titulo: 'Disponible',
            alinear: 'der',
            ancho: '7rem',
            celda: (p) => (
              <span className="mono fuerte">{decimal(d(p.id)?.disponible ?? '0')}</span>
            ),
          },
        ] satisfies Columna<ProductoEncontrado>[])
      : []),
  ];

  return (
    <div className="pantalla consulta" data-pantalla="productos">
      <div className="barra-filtros">
        <input
          autoFocus={activa}
          className="buscador"
          aria-label="Buscar productos"
          placeholder="SKU, nombre o código de barras…"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <label className="campo">
          <span className="etiqueta">Stock en</span>
          <select
            value={ubicacionId}
            onChange={(e) => setUbicacionId(e.target.value)}
            aria-label="Ubicación de stock"
          >
            <option value="">(elija ubicación)</option>
            {(ubicaciones.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.codigo} · {u.nombre} {u.tipo ? `(${u.tipo})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <div className="dividido">
        <Grilla
          etiqueta="Productos"
          columnas={columnas}
          filas={q.filas}
          clave={(p) => p.id}
          cargando={q.isFetching}
          mas={{ hayMas: q.hayMas, cargando: q.cargandoMas, alCargar: q.cargarMas }}
          vacio={texto.trim().length < 2 ? 'Escriba al menos 2 caracteres' : 'Sin resultados'}
          alSeleccionar={setSel}
          {...(alAbrirProducto ? { alAbrir: alAbrirProducto } : {})}
        />
        <div className="lateral">
          {sel ? (
            <>
              <h3>
                {sel.sku} · {sel.nombre}
                {alAbrirProducto && (
                  <button
                    type="button"
                    className="enlace abrir-ficha"
                    onClick={() => alAbrirProducto(sel)}
                    title="Abrir la ficha del producto en su pestaña (Enter)"
                  >
                    Abrir ficha
                  </button>
                )}
              </h3>
              <table className="grilla compacta" aria-label="Presentaciones">
                <thead>
                  <tr>
                    <th>Presentación</th>
                    <th>Unidad</th>
                    <th className="al-der">Factor</th>
                    <th>Vendible</th>
                  </tr>
                </thead>
                <tbody>
                  {(pres.data ?? []).map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.nombre} <span className="apagado">({p.codigo})</span>
                      </td>
                      <td>{p.unidad_codigo}</td>
                      <td className="al-der mono">{decimal(p.factor_a_base)}</td>
                      <td>{p.vendible ? 'sí' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablaStock
                empresaId={empresaId}
                productoId={sel.id}
                ubicaciones={ubicaciones.data ?? []}
                resaltar={ubicacionId || undefined}
                unidad={sel.unidad_base_codigo}
              />
            </>
          ) : (
            <p className="apagado">Seleccione un producto para ver presentaciones y stock.</p>
          )}
        </div>
      </div>
    </div>
  );
}
